import cx from 'classnames';
import { useOperaGuestProfile } from 'restaurantAdmin/reservations/service/hotelLink/hooks';
import typography from '~styles/typography.scss';
import styles from './HotelGuestProfile.scss';

const OperaLabel = () => (
  <span className={cx(typography.t1, styles.sourceLabel)}>Opera</span>
);

// The guest-level counterpart to HotelLinkSection (which shows tonight's stay in the occupant
// drawer). PRD section 4 line 96 asks for a read-only VIP Code field on the guest record; this
// is that, plus the rest of what Peak has learned from the hotel about this guest. Read-only by
// design: every value here came from Opera and is corrected in Opera, never in Peak. Peak's own
// tags and notes render in sibling sections and are never merged into these -- the union is
// visual, exactly as it is in the drawer.
export const HotelGuestProfile = ({ guestId }: { guestId: string }) => {
  const { data, isPending, isError } = useOperaGuestProfile(guestId);
  const profile = data?.data;

  // Absent, not empty: a guest who has never been matched to a hotel stay gets no section at
  // all, rather than an empty panel on every guest in the book. Loading and error states are
  // silent for the same reason -- this block is supplementary, not the page, so there is no
  // toast and no spinner competing with the rest of the guest profile.
  if (isPending || isError || !profile) return null;

  return (
    <section
      className={styles.container}
      aria-labelledby="hotel-profile-section"
    >
      <h3
        className={cx(typography.h7, styles.heading)}
        id="hotel-profile-section"
      >
        Hotel profile
      </h3>
      {profile.vipCode && (
        <span className={typography.t2}>
          VIP: {profile.vipCode}
          {profile.vipDescription ? ` ${profile.vipDescription}` : ''}{' '}
          <OperaLabel />
        </span>
      )}
      {profile.operaPhone && (
        <span className={typography.t2}>
          Phone: {profile.operaPhone} <OperaLabel />
        </span>
      )}
      {profile.operaEmail && (
        <span className={typography.t2}>
          Email: {profile.operaEmail} <OperaLabel />
        </span>
      )}
      {profile.operaAllergies && profile.operaAllergies.length > 0 && (
        <span className={typography.t2}>
          Allergies:{' '}
          {profile.operaAllergies.map((entry) => entry.value).join(', ')}{' '}
          <OperaLabel />
        </span>
      )}
      {profile.operaOccasion && (
        <span className={typography.t2}>
          Occasion: {profile.operaOccasion} <OperaLabel />
        </span>
      )}
      <span className={cx(typography.t2, styles.seen)}>
        Hotel stays linked {profile.firstLinkedDate} to {profile.lastLinkedDate}
      </span>
    </section>
  );
};
